import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import SubmitButton from 'Components/UI/SubmitButton';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import TextField from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/TextField';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { getBorder, getBorderRadius } from 'Components/utils';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    ...getBorder(theme, {
      all: true,
      color: theme.palette.facet.mediumGray,
    }),
    ...getBorderRadius(theme.radius.xs, { all: true }),
  },
  input: {
    margin: theme.spacing(1, 0),
  },
  formInfo: {
    paddingBottom: theme.spacing(2),
  },
  saveButton: {},
});

const TextInputGroup = ({
  className,
  classes,
  inputConfigs,
}) => {
  const { getQuestionnaireAnswer, putQuestionnaireAnswer } = useSwaggerlessContext();
  const { householdId } = useSimpleHousehold();
  const initialState = inputConfigs.reduce((acc, curr) => {
    acc[curr.answerKey] = '';

    return acc;
  }, {});
  const [isLoading, setIsLoading] = React.useState(true);
  const [state, setState] = React.useState(initialState);

  useComponentDidMount(async () => {
    try {
      const answerMap = {};

      for (const inputConfig of inputConfigs) {
        const { answerKey } = inputConfig;

        // eslint-disable-next-line no-await-in-loop
        const resp = await getQuestionnaireAnswer(householdId, answerKey);

        const ansValue = resp?.attributes?.value;
        const isReadonly = resp?.attributes?.isReadonly === 'true';

        answerMap[answerKey] = { val: ansValue || '', isReadonly };
      }

      setState({ ...state, ...answerMap });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }

    setIsLoading(false);
  });

  const checkIsValid = () => {
    return inputConfigs.reduce((acc, curr) => {
      if (!curr.isRequired) {
        return acc;
      }

      const currState = state[curr.answerKey].val;
      return acc && currState && currState.length > 0;
    }, true);
  };

  const handleSubmit = async () => {
    try {
      for (const inputConfig of inputConfigs) {
        const {
          answerKey,
          answerIndex = -1,
        } = inputConfig;

        // eslint-disable-next-line no-await-in-loop
        await putQuestionnaireAnswer(householdId, answerKey, {
          attributes: {
            value: state[answerKey].val,
            index: answerIndex,
          },
        });
      }

      displaySuccess('Save Successful');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <Typography
        className={classes.formInfo}
        variant={'h6'}
      >
        {'The following fields will only save if you click the Save button below.'}
      </Typography>
      {inputConfigs.map(({ label, answerKey, isRequired }) => {
        return (
          <TextField
            InputLabelProps={{ shrink: !!state[answerKey].val }} // Without this, the label will overlap with the value.  See https://stackoverflow.com/questions/68480218/material-ui-textfield-label-does-not-move-up-when-the-value-for-textfield-is-set
            className={classes.input}
            disabled={isLoading || state[answerKey].isReadonly}
            fullWidth
            helperText={(isRequired) ? '* Required' : null}
            key={answerKey}
            onChange={(value) => {
              setState({ ...state, [answerKey]: { ...state[answerKey], val: value } });
            }}
            variant="outlined"
            label={label}
            value={state[answerKey].val}
          />
        );
      })}
      <SubmitButton
        className={classes.saveButton}
        color="secondary"
        disabled={!checkIsValid()}
        disableElevation
        onClick={handleSubmit}
        variant="contained"
      />
    </Box>
  );
};

TextInputGroup.defaultProps = {
  className: '',
};

TextInputGroup.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  inputConfigs: PropTypes.array.isRequired,
};

export default withStyles(styles)(TextInputGroup);
